import React, { useEffect, useRef } from "react";
import "./Chatbot.css";

export interface ChatMessage {
  id: string;
  text: string;
  sender: "bot" | "user";
  timestamp: Date;
}

export interface QuickReply {
  text: string;
  action:
    | "primary"
    | "secondary"
    | "continue"
    | "gateway_yes"
    | "gateway_no"
    | "landlord_to_cas2"
    | "landlord_to_cas4";
}

export interface ConversationStep {
  bot: string;
  replies: QuickReply[];
}

export type SectionKey =
  | "CAS1"
  | "CAS1_LANDLORD"
  | "CAS2"
  | "CAS2_LANDLORD"
  | "CBS2"
  | "CAS3"
  | "CAS4";

export const CONVERSATION_SCRIPTS: Record<SectionKey, ConversationStep[]> = {
  // ═══════════════════════════════════════════════════════════
  // CONSUMER PATH
  // ═══════════════════════════════════════════════════════════

  // ─── CAS1 — Consumer welcome + survey gateway ──────────────────────────
  CAS1: [
    {
      // Step 0: Welcome
      bot: "Hi there! 👋 Alex and the team are busy building something special to launch early next year. They've decided to show you the plan of action. As you explore, they'd love to gauge feedback and interest at various points - any input would be hugely appreciated. Don't worry, I'll be here for any questions and to help you navigate further. Happy exploring!",
      replies: [
        { text: "Thanks, let's see what this is", action: "continue" },
        { text: "Sounds good", action: "continue" },
      ],
    },
    {
      // Step 1: Scroll prompt (auto-closes)
      bot: "Have a scroll down, take a look and I'll reach out later. 👇",
      replies: [],
    },
    {
      // Step 2: Product → Science bridge (triggered after CBS1 completion)
      bot: "So that's the product. Would you be interested in finding out more about the science behind it and how it actually helps users? This is what drives us and our purpose.",
      replies: [
        { text: "Yes, tell me more", action: "continue" },
        { text: "I'm curious", action: "continue" },
        { text: "Not really interested", action: "secondary" },
      ],
    },
    {
      // Step 3: Survey invite
      bot: "Before we proceed, we just want to take a minute to hear your thoughts on what you've seen so far. A short survey - all data anonymised. Are you up for it? And hey, at any point you can say 'I'm out' and we'll close the app with a huge thanks from us for taking the time.",
      replies: [
        { text: "Sure, let's do it", action: "primary" },
        { text: "I'm out, thanks", action: "secondary" },
      ],
    },
    {
      // Step 4: Gateway 1
      bot: "Thanks for sharing your thoughts! 🙏 One quick question - would you use something like this?",
      replies: [
        { text: "Yes", action: "gateway_yes" },
        { text: "No", action: "continue" },
      ],
    },
    {
      // Step 5: Gateway 2
      bot: "Fair enough. But imagine it was validated and became the thing to do - everyone was doing it. Would you use it then?",
      replies: [
        { text: "Yes", action: "gateway_yes" },
        { text: "No", action: "gateway_no" },
      ],
    },
  ],

  // ─── CAS2 — Consumer science / evidence ────────────────────────────────
  CAS2: [
    {
      bot: "Heavy stuff, right? Those stats about chronic stress and burnout... 😓",
      replies: [
        { text: "Yeah, I feel this daily", action: "continue" },
        { text: "It's eye-opening", action: "continue" },
      ],
    },
    {
      bot: "The thing is, cities have infrastructure for everything except genuine rest. Coffee shops for energy, gyms for release, bars for unwinding... but nothing for nervous system reset. That's the gap we're trying to fill.",
      replies: [
        { text: "Makes total sense", action: "continue" },
        { text: "How would that actually work?", action: "continue" },
      ],
    },
    {
      bot: "We're asking early supporters to pre-book their first session. It shows investors this isn't just theoretical - real people want this. No payment yet, just reserving your spot at founding member pricing. Want to see the options?",
      replies: [
        { text: "Yes, show me the details", action: "primary" },
        { text: "Not ready for that yet", action: "secondary" },
      ],
    },
  ],

  // ─── CBS2 — Consumer post-prebooking celebration ───────────────────────
  CBS2: [
    {
      bot: "YOU ABSOLUTE LEGEND! 🎉🎉🎉\n\nSeriously - most people scroll, nod, and leave. You just put your hand up and said \"I'm in.\" That takes guts and we love you for it.\n\nHere's the thing though - we're not actually taking payments right now. We're recording who's ready to commit so we can show our investors that real people want this. Your intent means everything.\n\nWhen we're ready to take bookings, you'll be the first to know - same founding member pricing, same priority access. Consider yourself a beta honorary founder. 🙌",
      replies: [
        { text: "Ha! Love it", action: "continue" },
        { text: "Smart move", action: "continue" },
      ],
    },
    {
      bot: "So now you've seen the product, the science, and shown you're serious... there's one more thing.\n\nWe're opening a family & friends funding round for people who want to actually own a piece of what we're building. It's early-stage, so there's risk - but also the chance to be part of something from the ground up.\n\nWant to see the full business case? No pressure at all.",
      replies: [
        { text: "Yes, show me everything", action: "primary" },
        { text: "I'm good for now, thanks", action: "secondary" },
      ],
    },
  ],

  // ─── CAS3 — Consumer founder story + category creation + investment ────
  CAS3: [
    {
      bot: "This idea started because we had nowhere to take a timeout with our kids whilst visiting London. It's stimulation overload - you're on your feet all day and when the kids begin to melt down you feel like a burden to everyone around you.\n\nBut sometimes I just don't want to be crammed in a disabled loo or pay £10 for a coffee just to sit for ten minutes at 5pm.\n\nWe've lost third spaces. Amenity is disappearing off our high streets. No one is building for us anymore - unless it's the next Soho House.\n\nFlex is my day job and bread and butter. We don't need another co-working space. We need places for us to live and function - as families, as humans. We don't need another vape shop filling the void.",
      replies: [
        { text: "I feel this", action: "continue" },
        { text: "Go on", action: "continue" },
      ],
    },
    {
      bot: "We're not trying to build another wellness brand. We're building infrastructure.\n\nThe gym became mainstream in the 1980s. The coffee shop as a third space in the 2000s. Co-working in the 2010s. Every decade, a new category of space gets invented that becomes part of how cities function.\n\nThis is that moment for nervous system recovery. And we want to own that category.",
      replies: [
        { text: "That's a bold claim", action: "continue" },
        { text: "I can see it", action: "continue" },
      ],
    },
    {
      bot: "Thank you for taking the time to read this far.\n\nIf you're as pumped as us and believe in this - if you're up for helping us achieve this vision and help millions manage their wellbeing - we'd love to have you as a founding investor.\n\nWe're opening this to a select group who've already backed us with action. The terms won't stay this way once institutional money comes in.\n\nDon't worry if the timing isn't right - thank you for pre-booking. We can't wait to share our progress and get you into your first session. The investment option will be available in your member dashboard whenever you're ready.",
      replies: [
        { text: "Yes, I want to be involved", action: "primary" },
        { text: "Thanks, just the session for now", action: "secondary" },
      ],
    },
  ],

  // ═══════════════════════════════════════════════════════════
  // LANDLORD PATH
  // ═══════════════════════════════════════════════════════════

  // ─── CAS1_LANDLORD — Welcome + evidence bridge ────────────────────────
  CAS1_LANDLORD: [
    {
      // Step 0: Welcome
      bot: "Hi there! 👋 Alex and the team have put together something specifically for you. This is the landlord and asset owner view - you'll see the product, the model, and the numbers. I'll be here to help you navigate. Happy exploring!",
      replies: [
        { text: "Let's see it", action: "continue" },
        { text: "Sounds good", action: "continue" },
      ],
    },
    {
      // Step 1: Scroll prompt (auto-closes)
      bot: "Have a scroll down, take a look and I'll reach out shortly. 👇",
      replies: [],
    },
    {
      // Step 2: Evidence bridge (triggered by CAS1 bottom re-trigger)
      bot: "So that's the product — from the user's perspective. Want to understand the wellbeing science behind it? It's what makes this credible to the people walking into your space.",
      replies: [
        { text: "Yes, show me the evidence", action: "landlord_to_cas2" },
        { text: "Not for me right now", action: "secondary" },
      ],
    },
  ],

  // ─── CAS2_LANDLORD — Same evidence beats, bridge to CAS4 ─────────────
  CAS2_LANDLORD: [
    {
      // Step 0: Same opening as consumer
      bot: "Heavy stuff, right? Those stats about chronic stress and burnout... 😓",
      replies: [
        { text: "Yeah, I feel this daily", action: "continue" },
        { text: "It's eye-opening", action: "continue" },
      ],
    },
    {
      // Step 1: Same infrastructure gap as consumer
      bot: "The thing is, cities have infrastructure for everything except genuine rest. Coffee shops for energy, gyms for release, bars for unwinding... but nothing for nervous system reset. That's the gap we're trying to fill.",
      replies: [
        { text: "Makes total sense", action: "continue" },
        { text: "How would that actually work?", action: "continue" },
      ],
    },
    {
      // Step 2: LANDLORD — bridge to CAS4 (not pre-booking)
      bot: "That's the evidence base. Built on peer-reviewed science - this isn't wellness theatre, it genuinely works. Now let's show you what it looks like as a business opportunity in your asset.",
      replies: [
        { text: "Show me the numbers", action: "landlord_to_cas4" },
        { text: "Let's go", action: "landlord_to_cas4" },
      ],
    },
  ],

  // ─── CAS4 — Landlord close ────────────────────────────────────────────
  CAS4: [
    {
      bot: "Quick pulse check before we go further. What's your honest outlook for retail and hospitality - rate hikes, spiralling wage costs, cost pressures, red tape, suppressed consumer spend. Where does it land for you?",
      replies: [
        {
          text: "It's a joke - surprised anyone survives",
          action: "continue",
        },
        { text: "Bleak, but we hold fast", action: "continue" },
        {
          text: "Independents are done - opportunity for the chains",
          action: "continue",
        },
        {
          text: "Survival of the strongest - I still see rental growth",
          action: "continue",
        },
      ],
    },
    {
      bot: "We've looked hard at this. The high street isn't going anywhere - even if certain operators are. The coffee shop struggles. Co-working margins are shot. What survives needs to be micro, autonomous, low on operational cost and built around a clear purpose. This is how you fight back. We need the high street. We need amenity more than ever. Cities need this. It starts with something small - and there's so much opportunity to grow from there. This won't be everywhere forever. Don't miss the window.",
      replies: [
        { text: "Tell me more", action: "continue" },
        { text: "I see it", action: "continue" },
      ],
    },
    {
      bot: "You've seen the model. You've seen the numbers. If you have a space - or know someone who does - this is the conversation worth having. Alex is available for a direct conversation. No deck, no hard sell. Just a straight talk about whether there's something here for you.",
      replies: [
        { text: "Book a conversation with Alex", action: "primary" },
        { text: "Thanks, not for me right now", action: "secondary" },
      ],
    },
  ],
};

interface ChatbotProps {
  isOpen: boolean;
  messages: ChatMessage[];
  quickReplies: QuickReply[];
  showNotification: boolean;
  showPrompt: boolean;
  promptText: string;
  onOpen: () => void;
  onClose: () => void;
  onPromptClick: () => void;
  onReplyClick: (reply: QuickReply) => void;
}

const Chatbot: React.FC<ChatbotProps> = ({
  isOpen,
  messages,
  quickReplies,
  showNotification,
  showPrompt,
  promptText,
  onOpen,
  onClose,
  onPromptClick,
  onReplyClick,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <>
      {!isOpen && (
        <button
          className={`chatbot-bubble ${showPrompt ? "prompt-active" : ""}`}
          onClick={showPrompt ? onPromptClick : onOpen}
          aria-label="Open chat"
        >
          <span className="bubble-icon">💬</span>
          <span className="bubble-pulse"></span>

          {showNotification && !showPrompt && (
            <span className="bubble-notification">
              <span className="notification-text">Hi!</span>
            </span>
          )}

          {showPrompt && (
            <span className="bubble-prompt">
              <span className="prompt-text">{promptText}</span>
            </span>
          )}
        </button>
      )}

      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="bot-info">
              <div className="bot-avatar">🤖</div>
              <div className="bot-details">
                <div className="bot-name">LOA Assistant</div>
                <div className="bot-status">
                  <span className="status-dot"></span>
                  Online
                </div>
              </div>
            </div>
            <button
              className="close-button"
              onClick={onClose}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.sender}`}>
                {message.sender === "bot" && (
                  <div className="message-avatar">🤖</div>
                )}
                <div className="message-bubble">{message.text}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {quickReplies.length > 0 && (
            <div className="chatbot-quick-replies">
              {quickReplies.map((reply, index) => (
                <button
                  key={index}
                  className="quick-reply-button"
                  onClick={() => onReplyClick(reply)}
                >
                  {reply.text}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
